import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Loader from '../components/ui/Loader';
import { useAuth } from '../components/shared/AuthContext';
import ErrorMessage from '../components/shared/ErrorMessage';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getTodayDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${year}-${month}-${day}`; // Fixed typo: was `${year}-${month}-${year}`
};

const FieldOfficerSwapping = () => {
  const { permissions, roleName, data } = useAuth();
  const isSuperAdmin = roleName === 'superadmin';
  const userBranchId = data?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  // Core data state
  const [allStaffData, setAllStaffData] = useState([]);
  const [allCustomerData, setAllCustomerData] = useState([]);
  const [allMemberData, setAllMemberData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [targetFieldOfficer, setTargetFieldOfficer] = useState('');
  const [substituteFieldOfficer, setSubstituteFieldOfficer] = useState('');
  const [swapMode, setSwapMode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Memoized computed data
  const fieldOfficers = useMemo(() => {
    if (!dataLoaded) return [];

    let filteredStaff = allStaffData.filter(staff => staff.roleId === 3);

    if (shouldFilterByBranch) {
      filteredStaff = filteredStaff.filter(staff => staff.branchId === userBranchId);
    }

    // Match and write officers by staff_details.id (the FK target), never staffId:
    // the two have drifted for some imported staff, e.g. id 85 / staffId 72.
    return filteredStaff.map(fo => {
      const customers = allCustomerData
        .filter(customer => parseInt(customer.fieldOfficerId) === parseInt(fo.id))
        .map(customer => {
          const member = allMemberData.find(m => m.customerId === customer.id);
          return {
            ...customer,
            memberId: member?.id || null,
            memberStatus: member?.status || 'N/A',
            previousFieldOfficerName: allStaffData.find(staff => String(staff.id) === String(customer.previous_field_officer))?.name || 'N/A',
            transferredFieldOfficerName: allStaffData.find(staff => String(staff.id) === String(customer.transferred_field_officer))?.name || 'N/A',
            substituteFieldOfficerName: allStaffData.find(staff => String(staff.id) === String(customer.substitute_field_officer))?.name || 'N/A'
          };
        });

      return {
        ...fo,
        customers: customers
      };
    });
  }, [allStaffData, allCustomerData, allMemberData, dataLoaded, shouldFilterByBranch, userBranchId]);

  // Filtered field officers based on selection
  const filteredFieldOfficers = useMemo(() => {
    if (!selectedFieldOfficer || selectedFieldOfficer === '') {
      // No filter - show all field officers
      return fieldOfficers;
    }

    // Filter to show only the selected field officer
    const selectedId = parseInt(selectedFieldOfficer, 10);
    return fieldOfficers.filter(fo => fo.id === selectedId);
  }, [fieldOfficers, selectedFieldOfficer]);

  // Pagination - with customer-level search filtering
  const { currentRows, totalPages, totalRecords } = useMemo(() => {
    let allRows = filteredFieldOfficers.flatMap((fo, foIndex) =>
      fo.customers.length > 0
        ? fo.customers.map((customer, custIndex) => ({
          ...customer,
          fieldOfficer: fo,
          globalIndex: foIndex * fo.customers.length + custIndex
        }))
        : [{
          id: `no-customers-${fo.id}`,
          fieldOfficer: fo,
          globalIndex: foIndex,
          isEmptyRow: true
        }]
    );

    // Apply customer-level search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      allRows = allRows.filter(row => {
        if (row.isEmptyRow) return false;

        // Search in customer fields - with proper null checks
        const customerNameMatch = row.name ? String(row.name).toLowerCase().includes(term) : false;
        const customerIdMatch = row.customerUniqueId ? String(row.customerUniqueId).toLowerCase().includes(term) : false;
        const customerNumericIdMatch = row.id ? String(row.id).includes(term) : false;
        const customerPhoneMatch = row.mobileNumber ? String(row.mobileNumber).includes(term) : false;
        const customerAltPhoneMatch = row.altMobileNumber ? String(row.altMobileNumber).includes(term) : false;

        // Search in field officer fields
        const foNameMatch = row.fieldOfficer?.name ? String(row.fieldOfficer.name).toLowerCase().includes(term) : false;
        const foStaffIdMatch = row.fieldOfficer?.staffId ? String(row.fieldOfficer.staffId).includes(term) : false;
        const foPhoneMatch = row.fieldOfficer?.phoneNo ? String(row.fieldOfficer.phoneNo).includes(term) : false;

        return customerNameMatch || customerIdMatch || customerNumericIdMatch ||
          customerPhoneMatch || customerAltPhoneMatch ||
          foNameMatch || foStaffIdMatch || foPhoneMatch;
      });
    }

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = allRows.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(allRows.length / rowsPerPage);

    return { currentRows, totalPages, totalRecords: allRows.length };
  }, [filteredFieldOfficers, currentPage, rowsPerPage, searchTerm]);

  // Initial data fetch using dedicated API
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Use the dedicated Field Officer Swap Data API
      let apiUrl = `${API_BASE_URL}/api/v2/field-officer-swap-data`;
      if (shouldFilterByBranch) {
        apiUrl += `?branchId=${userBranchId}`;
      }

      const response = await fetch(apiUrl);

      if (!response.ok) throw new Error('Failed to fetch field officer swap data');

      const result = await response.json();

      if (result.status === 'success') {
        setAllStaffData(result.data.staffData || []);
        setAllCustomerData(result.data.customerData || []);
        setAllMemberData(result.data.memberData || []);
        setDataLoaded(true);
        setErrorMsg('');
      } else {
        throw new Error(result.message || 'Failed to load data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMsg('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [shouldFilterByBranch, userBranchId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFieldOfficer, searchTerm]);

  // Event handlers
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleFieldOfficerChange = (e) => {
    setSelectedFieldOfficer(e.target.value);
    setSelectedCustomers([]);
    setTargetFieldOfficer('');
    setSubstituteFieldOfficer('');
  };

  const handleCustomerSelection = (customerId, isSelected) => {
    if (isSelected) {
      setSelectedCustomers(prev => [...prev, customerId]);
    } else {
      setSelectedCustomers(prev => prev.filter(id => id !== customerId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allCustomerIds = currentRows
        .filter(row => !row.isEmptyRow)
        .map(row => row.id);
      setSelectedCustomers(allCustomerIds);
    } else {
      setSelectedCustomers([]);
    }
  };

  const refreshData = useCallback(async () => {
    setDataLoaded(false);
    setSelectedCustomers([]);
    setTargetFieldOfficer('');
    setSubstituteFieldOfficer('');
    await fetchAllData();
  }, [fetchAllData]);

  const handleSwapFieldOfficers = async () => {
    if (!selectedFieldOfficer || (!targetFieldOfficer && !substituteFieldOfficer)) {
      alert('Please select a source field officer and at least one target or substitute field officer');
      return;
    }

    if (selectedFieldOfficer === targetFieldOfficer) {
      alert('Source and target field officers cannot be the same');
      return;
    }

    if (selectedFieldOfficer === substituteFieldOfficer) {
      alert('Source and substitute field officers cannot be the same');
      return;
    }

    const sourceFieldOfficer = fieldOfficers.find(fo => fo.id === parseInt(selectedFieldOfficer));
    const targetFieldOfficerData = targetFieldOfficer
      ? fieldOfficers.find(fo => fo.id === parseInt(targetFieldOfficer))
      : null;
    const substituteFieldOfficerData = substituteFieldOfficer
      ? fieldOfficers.find(fo => fo.id === parseInt(substituteFieldOfficer))
      : null;

    if (!sourceFieldOfficer) {
      alert('Source field officer not found');
      return;
    }

    const customerCount = sourceFieldOfficer.customers.length;
    if (customerCount === 0) {
      alert('No customers to transfer');
      return;
    }

    const targetName = targetFieldOfficerData ? targetFieldOfficerData.name : 'no target';
    const substituteName = substituteFieldOfficerData ? ` with substitute ${substituteFieldOfficerData.name}` : '';
    if (window.confirm(`Are you sure you want to transfer ${customerCount} customer(s) from ${sourceFieldOfficer.name} to ${targetName}${substituteName}?`)) {
      try {
        setLoading(true);

        const updatePromises = sourceFieldOfficer.customers.map(customer => {
          const updateData = {
            model: 'personal_details',
            type: 'Edit',
            id: customer.id,
            previous_field_officer: sourceFieldOfficer.id,
            salary_Date: getTodayDate()
          };

          if (targetFieldOfficerData) {
            updateData.fieldOfficerId = targetFieldOfficerData.id;
            updateData.transferred_field_officer = targetFieldOfficerData.id;
          }

          if (substituteFieldOfficerData) {
            updateData.substitute_field_officer = substituteFieldOfficerData.id;
          } else if (!targetFieldOfficerData) {
            updateData.substitute_field_officer = null;
          }

          return axios.post(`${API_BASE_URL}/api/addDetails`, updateData, { baseURL: API_BASE_URL });
        });

        const results = await Promise.allSettled(updatePromises);
        const failedUpdates = results.filter(result => result.status === 'rejected');

        if (failedUpdates.length > 0) {
          console.error('Failed updates:', failedUpdates);
          throw new Error(`${failedUpdates.length} updates failed`);
        }

        alert('Field officers swapped successfully!');
        setErrorMsg('');
        await refreshData();
        window.location.reload(); // Force hard refresh
      } catch (error) {
        console.error('Error swapping field officers:', error);
        setErrorMsg('Failed to swap field officers: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSwapCustomers = async () => {
    if (!selectedFieldOfficer || (!targetFieldOfficer && !substituteFieldOfficer) || selectedCustomers.length === 0) {
      alert('Please select source field officer, at least one target or substitute field officer, and at least one customer');
      return;
    }

    if (selectedFieldOfficer === targetFieldOfficer) {
      alert('Source and target field officers cannot be the same');
      return;
    }

    if (selectedFieldOfficer === substituteFieldOfficer) {
      alert('Source and substitute field officers cannot be the same');
      return;
    }

    const sourceFieldOfficer = fieldOfficers.find(fo => fo.id === parseInt(selectedFieldOfficer));
    const targetFieldOfficerData = targetFieldOfficer
      ? fieldOfficers.find(fo => fo.id === parseInt(targetFieldOfficer))
      : null;
    const substituteFieldOfficerData = substituteFieldOfficer
      ? fieldOfficers.find(fo => fo.id === parseInt(substituteFieldOfficer))
      : null;

    if (!sourceFieldOfficer) {
      alert('Field officer not found');
      return;
    }

    const targetName = targetFieldOfficerData ? targetFieldOfficerData.name : 'no target';
    const substituteName = substituteFieldOfficerData ? ` with substitute ${substituteFieldOfficerData.name}` : '';
    if (window.confirm(`Are you sure you want to move ${selectedCustomers.length} customer(s) to ${targetName}${substituteName}?`)) {
      try {
        setLoading(true);

        const updatePromises = selectedCustomers.map(customerId => {
          const customer = sourceFieldOfficer.customers.find(c => c.id === customerId);
          const updateData = {
            model: 'personal_details',
            type: 'Edit',
            id: customerId,
            previous_field_officer: sourceFieldOfficer.id,
            salary_Date: getTodayDate()
          };

          if (targetFieldOfficerData) {
            updateData.fieldOfficerId = targetFieldOfficerData.id;
            updateData.transferred_field_officer = targetFieldOfficerData.id;
          }

          if (substituteFieldOfficerData) {
            updateData.substitute_field_officer = substituteFieldOfficerData.id;
          } else if (!targetFieldOfficerData) {
            updateData.substitute_field_officer = null;
          }

          return axios.post(`${API_BASE_URL}/api/addDetails`, updateData, { baseURL: API_BASE_URL });
        });

        const results = await Promise.allSettled(updatePromises);
        const failedUpdates = results.filter(result => result.status === 'rejected');

        if (failedUpdates.length > 0) {
          console.error('Failed updates:', failedUpdates);
          throw new Error(`${failedUpdates.length} updates failed`);
        }

        alert('Customers moved successfully!');
        setErrorMsg('');
        await refreshData();
        window.location.reload(); // Force hard refresh
      } catch (error) {
        console.error('Error moving customers:', error);
        setErrorMsg('Failed to move customers: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const exportToExcel = () => {
    const data = filteredFieldOfficers.flatMap((fo, foIndex) =>
      fo.customers.length > 0
        ? fo.customers.map((customer, custIndex) => ({
          'S.No': foIndex * fo.customers.length + custIndex + 1,
          'Field Officer ID': fo.id,
          'Field Officer Staff ID': fo.staffId,
          'Field Officer Name': fo.name || 'N/A',
          'Field Officer Phone': fo.phoneNo || 'N/A',
          'Customer ID': customer.customerUniqueId,
          'Customer Name': customer.name,
          'Customer Phone': customer.mobileNumber,
          'Customer Address': customer.currentStreetAddress || customer.permanentStreetAddress || 'N/A',
          'Member Status': customer.memberStatus,
          'Previous Field Officer': customer.previousFieldOfficerName,
          'Transferred Field Officer': customer.transferredFieldOfficerName,
          'Substitute Field Officer': customer.substituteFieldOfficerName
        }))
        : [{
          'S.No': foIndex + 1,
          'Field Officer ID': fo.id,
          'Field Officer Staff ID': fo.staffId,
          'Field Officer Name': fo.name || 'N/A',
          'Field Officer Phone': fo.phoneNo || 'N/A',
          'Customer ID': 'No customers',
          'Customer Name': 'No customers',
          'Customer Phone': 'No customers',
          'Customer Address': 'No customers',
          'Member Status': 'No customers',
          'Previous Field Officer': 'N/A',
          'Transferred Field Officer': 'N/A',
          'Substitute Field Officer': 'N/A'
        }]
    );

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Field Officers and Customers');
    XLSX.writeFile(workbook, `Field_Officers_and_Customers_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const availableTargetOfficers = fieldOfficers.filter(fo =>
    fo.id !== parseInt(selectedFieldOfficer || 0)
  );

  const availableSubstituteOfficers = fieldOfficers.filter(fo =>
    fo.id !== parseInt(selectedFieldOfficer || 0) &&
    fo.id !== parseInt(targetFieldOfficer || 0)
  );

  if (!dataLoaded && loading) {
    return <Loader message="Loading field officer data..." />;
  }

  return (
    <div className="container-fluid p-0">
      {loading && <Loader message="Processing request..." />}
      <ErrorMessage message={errorMsg} />

      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center py-2.5 px-3">
          <h5 className="mb-0 text-ink-900 font-bold" style={{ fontSize: '15px' }}>
            <i className="fas fa-filter me-2 text-primary"></i>
            Filters & Actions
          </h5>
          <div className="d-flex gap-2">
            <button
              onClick={refreshData}
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              disabled={loading}
              style={{ height: '32px', fontSize: '12px', fontWeight: '600' }}
            >
              <i className="fas fa-sync-alt me-1.5"></i>
              Refresh
            </button>
            <button
              onClick={exportToExcel}
              className="btn btn-success btn-sm d-flex align-items-center"
              disabled={loading}
              style={{ height: '32px', fontSize: '12px', fontWeight: '600' }}
            >
              <i className="fas fa-file-excel me-1.5"></i>
              Export Excel
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6 col-lg-4">
              <div className="relative mt-1">
                <label className="absolute -top-2.5 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                  Search Customer
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-white" style={{ borderRight: 'none', height: '38px' }}>
                    <i className="fas fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name, Customer ID, Phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ borderLeft: 'none', height: '38px', fontSize: '13px' }}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm('')}
                      style={{ borderLeft: 'none', height: '38px' }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              {searchTerm && (
                <small className="text-muted mt-1 d-block ms-1" style={{ fontSize: '11px' }}>
                  Found {totalRecords} result(s)
                </small>
              )}
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="relative mt-1">
                <label className="absolute -top-2.5 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                  Field Officer
                </label>
                <select
                  value={selectedFieldOfficer}
                  onChange={handleFieldOfficerChange}
                  className="form-select"
                  style={{ height: '38px', fontSize: '13px' }}
                >
                  <option value="">All Field Officers</option>
                  {fieldOfficers.map((fo) => (
                    <option key={fo.id} value={fo.id}>
                      {fo.name || 'N/A'} (ID: {fo.id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="relative mt-1">
                <label className="absolute -top-2.5 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                  Swap Mode
                </label>
                <select
                  value={swapMode || ''}
                  onChange={(e) => {
                    setSwapMode(e.target.value || null);
                    setSelectedCustomers([]);
                    setTargetFieldOfficer('');
                    setSubstituteFieldOfficer('');
                  }}
                  className="form-select"
                  style={{ height: '38px', fontSize: '13px' }}
                >
                  <option value="">Select Mode</option>
                  <option value="fieldOfficer">Transfer All Customers</option>
                  <option value="customer">Transfer Selected Customers</option>
                </select>
              </div>
            </div>

            {swapMode && (
              <>
                <div className="col-md-6 col-lg-6">
                  <div className="relative mt-1">
                    <label className="absolute -top-2.5 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                      Target Field Officer (Optional)
                    </label>
                    <select
                      value={targetFieldOfficer}
                      onChange={(e) => {
                        setTargetFieldOfficer(e.target.value);
                        if (substituteFieldOfficer === e.target.value) {
                          setSubstituteFieldOfficer('');
                        }
                      }}
                      className="form-select"
                      style={{ height: '38px', fontSize: '13px' }}
                    >
                      <option value="">Select Target</option>
                      {availableTargetOfficers.map((fo) => (
                        <option key={fo.id} value={fo.id}>
                          {fo.name || 'N/A'} (ID: {fo.id})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-6 col-lg-6">
                  <div className="relative mt-1">
                    <label className="absolute -top-2.5 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                      Substitute Field Officer (Optional)
                    </label>
                    <select
                      value={substituteFieldOfficer}
                      onChange={(e) => setSubstituteFieldOfficer(e.target.value)}
                      className="form-select"
                      style={{ height: '38px', fontSize: '13px' }}
                    >
                      <option value="">None</option>
                      {availableSubstituteOfficers.map((fo) => (
                        <option key={fo.id} value={fo.id}>
                          {fo.name || 'N/A'} (ID: {fo.id})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          {swapMode && (
            <div className="row mt-3">
              <div className="col-12">
                <div className="d-flex gap-2 flex-wrap">
                  {swapMode === 'fieldOfficer' && (
                    <button
                      onClick={handleSwapFieldOfficers}
                      disabled={!selectedFieldOfficer || (!targetFieldOfficer && !substituteFieldOfficer) || loading}
                      className="btn btn-warning"
                    >
                      <i className="fas fa-exchange-alt me-1"></i>
                      Transfer All Customers
                    </button>
                  )}

                  {swapMode === 'customer' && (
                    <button
                      onClick={handleSwapCustomers}
                      disabled={!selectedFieldOfficer || (!targetFieldOfficer && !substituteFieldOfficer) || selectedCustomers.length === 0 || loading}
                      className="btn btn-info"
                    >
                      <i className="fas fa-user-friends me-1"></i>
                      Transfer Selected ({selectedCustomers.length})
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-3 col-sm-6">
          <div className="card shadow-xs" style={{ backgroundColor: '#E9DFFF', border: '1px solid rgba(124, 58, 237, 0.25)', borderRadius: '0.75rem' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title text-uppercase font-semibold" style={{ color: '#7C3AED', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    {selectedFieldOfficer ? 'Selected Field Officer' : 'Total Field Officers'}
                  </h6>
                  <h3 className="mb-0 font-black" style={{ color: '#7C3AED', fontWeight: 800 }}>
                    {filteredFieldOfficers.length}
                  </h3>
                </div>
                <div className="align-self-center">
                  <i className="fas fa-user-tie fa-2x" style={{ color: '#7C3AED', opacity: 0.75 }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card shadow-xs" style={{ backgroundColor: '#DDF5E6', border: '1px solid rgba(22, 163, 74, 0.25)', borderRadius: '0.75rem' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title text-uppercase font-semibold" style={{ color: '#16A34A', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    {selectedFieldOfficer ? 'Customers of Selected FO' : 'Total Customers'}
                  </h6>
                  <h3 className="mb-0 font-black" style={{ color: '#16A34A', fontWeight: 800 }}>
                    {filteredFieldOfficers.reduce((sum, fo) => sum + fo.customers.length, 0)}
                  </h3>
                </div>
                <div className="align-self-center">
                  <i className="fas fa-users fa-2x" style={{ color: '#16A34A', opacity: 0.75 }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card shadow-xs" style={{ backgroundColor: '#FFF0C7', border: '1px solid rgba(217, 119, 6, 0.25)', borderRadius: '0.75rem' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title text-uppercase font-semibold" style={{ color: '#D97706', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    Filtered Records
                  </h6>
                  <h3 className="mb-0 font-black" style={{ color: '#D97706', fontWeight: 800 }}>
                    {totalRecords}
                  </h3>
                </div>
                <div className="align-self-center">
                  <i className="fas fa-filter fa-2x" style={{ color: '#D97706', opacity: 0.75 }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card shadow-xs" style={{ backgroundColor: '#D9F3F7', border: '1px solid rgba(8, 145, 178, 0.25)', borderRadius: '0.75rem' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="card-title text-uppercase font-semibold" style={{ color: '#0891B2', fontSize: '0.75rem', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    Selected for Transfer
                  </h6>
                  <h3 className="mb-0 font-black" style={{ color: '#0891B2', fontWeight: 800 }}>
                    {selectedCustomers.length}
                  </h3>
                </div>
                <div className="align-self-center">
                  <i className="fas fa-check-square fa-2x" style={{ color: '#0891B2', opacity: 0.75 }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ color: '#000000' }}>Field Officers & Customers</h5>
          <div className="d-flex align-items-center gap-3">
            <small className="text-muted">
              Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} records
            </small>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 small">Rows:</label>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
              >
                {[5, 10, 20, 50, 100].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-white">
              <tr>
                <th>S.No</th>
                <th>Field Officer</th>
                <th>Officer ID</th>
                <th>Staff ID</th>
                <th>Phone</th>
                <th>Customer ID</th>
                <th>Customer Name</th>
                <th>Phone Number</th>
                <th>Address</th>
                <th>Member Status</th>
                <th>Previous FO</th>
                <th>Transferred FO</th>
                <th>Substitute FO</th>
                {swapMode === 'customer' && (
                  <th>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedCustomers.length > 0 &&
                          currentRows.filter(row => !row.isEmptyRow).every(row => selectedCustomers.includes(row.id))}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                      <label className="form-check-label small">All</label>
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={swapMode === 'customer' ? 14 : 13} className="text-center py-8">
                    <Loader size="md" />
                  </td>
                </tr>
              ) : currentRows.length > 0 ? (
                currentRows.map((row, index) => (
                  <tr key={row.id} className={row.isEmptyRow ? 'table-light' : ''}>
                    <td data-label="S.No">{((currentPage - 1) * rowsPerPage) + index + 1}</td>
                    <td data-label="Field Officer">
                      <div className="d-flex align-items-center justify-content-end justify-content-md-start">
                        <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '26px', height: '26px', fontSize: '11px', minWidth: '26px' }}>
                          {row.fieldOfficer.name?.charAt(0) || 'N'}
                        </div>
                        <div className="text-end text-md-start">
                          <div className="fw-bold" style={{ fontSize: '12px', lineHeight: '1.2' }}>{row.fieldOfficer.name || 'N/A'}</div>
                          <small className="text-muted" style={{ fontSize: '10px' }}>{row.fieldOfficer.customers.length} customers</small>
                        </div>
                      </div>
                    </td>
                    <td data-label="Officer ID">
                      <span className="badge bg-primary opacity-75">{row.fieldOfficer.id}</span>
                    </td>
                    <td data-label="Staff ID">
                      <span className="badge bg-primary opacity-75">{row.fieldOfficer.staffId}</span>
                    </td>
                    <td data-label="Phone">{row.fieldOfficer.phoneNo || 'N/A'}</td>

                    {row.isEmptyRow ? (
                      <td data-label="Customer Info" colSpan={swapMode === 'customer' ? 9 : 8} className="text-center text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        No customers assigned
                      </td>
                    ) : (
                      <>
                        <td data-label="Customer ID">
                          <span className="badge bg-success">{row.customerUniqueId}</span>
                        </td>
                        <td data-label="Customer Name">
                          <div className="fw-bold">{row.name}</div>
                        </td>
                        <td data-label="Phone Number">{row.mobileNumber || 'N/A'}</td>
                        <td data-label="Address">
                          <div className="text-truncate" style={{ maxWidth: '200px' }}>
                            {row.currentStreetAddress || row.permanentStreetAddress || 'N/A'}
                          </div>
                        </td>
                        <td data-label="Member Status">
                          <span className={`badge ${row.memberStatus === 'Active' ? 'bg-success' :
                            row.memberStatus === 'Inactive' ? 'bg-danger' : 'bg-secondary'
                            }`}>
                            {row.memberStatus}
                          </span>
                        </td>
                        <td data-label="Previous FO">{row.previousFieldOfficerName}</td>
                        <td data-label="Transferred FO">{row.transferredFieldOfficerName}</td>
                        <td data-label="Substitute FO">{row.substituteFieldOfficerName}</td>
                        {swapMode === 'customer' && (
                          <td data-label="Selection">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedCustomers.includes(row.id)}
                                onChange={(e) => handleCustomerSelection(row.id, e.target.checked)}
                              />
                            </div>
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={swapMode === 'customer' ? 14 : 13} className="text-center py-4">
                    <div className="text-muted">
                      <i className="fas fa-search fa-2x mb-2"></i>
                      <div>No data found</div>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="card-footer py-2">
            <nav aria-label="Table pagination">
              <ul className="pagination pagination-sm justify-content-center mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    style={{ fontSize: '11px', padding: '4px 10px' }}
                  >
                    First
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{ fontSize: '11px', padding: '4px 10px' }}
                  >
                    Previous
                  </button>
                </li>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNumber = Math.max(1, currentPage - 2) + i;
                  if (pageNumber > totalPages) return null;
                  return (
                    <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNumber)}
                        style={{ fontSize: '11px', padding: '4px 10px' }}
                      >
                        {pageNumber}
                      </button>
                    </li>
                  );
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{ fontSize: '11px', padding: '4px 10px' }}
                  >
                    Next
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{ fontSize: '11px', padding: '4px 10px' }}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      <style jsx>{`
        .avatar-sm {
          width: 32px;
          height: 32px;
          font-size: 14px;
          font-weight: bold;
        }
        
        .card {
          border: none;
          border-radius: 12px;
        }
        
        .card-header {
          border-radius: 12px 12px 0 0 !important;
          border-bottom: 1px solid #e9ecef;
        }
        
        .table th {
          border-top: none;
          font-weight: 600;
          font-size: 0.875rem;
          padding: 1rem 0.75rem;
        }
        
        .table td {
          padding: 0.75rem;
          vertical-align: middle;
        }
        
        .table-hover tbody tr:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }
        
        .btn {
          border-radius: 8px;
          font-weight: 500;
        }
        
        .form-select, .form-control {
          border-radius: 8px;
        }
        
        .badge {
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .shadow-sm {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
        }
        
        .text-primary {
          color: #0d6efd !important;
        }
        
        .bg-primary {
          background-color: #0d6efd !important;
        }
        
        .bg-success {
          background-color: #198754 !important;
        }
        
        .bg-info {
          background-color: #0dcaf0 !important;
        }
        
        .bg-warning {
          background-color: #ffc107 !important;
        }
        
        .pagination .page-link {
          border-radius: 6px;
          margin: 0 2px;
          border: 1px solid #dee2e6;
        }
        
        .pagination .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        
        .spinner-border {
          width: 2rem;
          height: 2rem;
        }
        
        @media (max-width: 768px) {
          .card-body .row.g-3 > .col-md-6 {
            margin-bottom: 1rem;
          }
          
          .d-flex.gap-2 {
            flex-wrap: wrap;
          }
          
          .table-responsive {
            font-size: 0.875rem;
          }
          
          .avatar-sm {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }
        }
        
        .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        
        .table-dark th {
          background-color: #212529;
          border-color: #32383e;
        }
        
        .input-group-text {
          background-color: #f8f9fa;
          border-color: #ced4da;
        }
        
        .text-truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

export default FieldOfficerSwapping;